import { G, Image, Link, Path, Svg, Text, View } from '@react-pdf/renderer';
import { Style } from '@react-pdf/types';
import * as React from 'react';

import { CurrencyCode } from '__generated-api__';
import { getDisplayPrice } from 'my-account/utils/currency';
import ImageBlockImage from 'spb-pdf/components/ImageBlock';
import { ProductProps } from 'spb-pdf/utils/product';

import leadFreeImage from 'assets/certs/lead_free.jpg';

const ProductFeatures: React.VFC<
  Pick<ProductProps, 'features' | 'show_features'> & { style?: Style | Style[]; condensed?: boolean }
> = ({ features, show_features, style, condensed }) => {
  if (!show_features || !features.length) {
    return null;
  }

  return (
    <View style={style}>
      <Text
        style={{
          fontFamily: 'Gotham Rounded',
          fontSize: 5,
          color: '#666666',
          opacity: 0.6,
          textTransform: 'uppercase',
          flexShrink: 0,
        }}
      >
        Features:
      </Text>
      {features.map((feature, index) => (
        <Text
          key={index}
          style={{
            fontFamily: 'Gotham Rounded',
            fontSize: 8,
            color: '#000000',
            opacity: 0.8,
            paddingTop: condensed && index !== 0 ? 3 : 5,
            flexShrink: 0,
          }}
        >
          {feature}
        </Text>
      ))}
    </View>
  );
};

const ProductNotes: React.VFC<Pick<ProductProps, 'notes' | 'heading' | 'show_info'> & { style?: Style | Style[] }> = ({
  show_info,
  notes = '',
  heading = '',
  style,
}) => {
  if (!show_info || (!notes.length && !heading.length)) {
    return null;
  }

  return (
    <View style={style}>
      {heading.length > 0 && (
        <Text
          style={{
            fontFamily: 'Gotham Rounded',
            fontSize: 5,
            color: '#666666',
            opacity: 0.6,
            textTransform: 'uppercase',
            flexShrink: 0,
          }}
        >
          {heading}
        </Text>
      )}
      {notes.length > 0 && (
        <Text
          style={{
            paddingTop: heading.length ? 5 : 0,
            fontFamily: 'Gotham Rounded',
            fontSize: 8,
            color: '#000000',
            opacity: 0.8,
            flexShrink: 0,
          }}
        >
          {notes}
        </Text>
      )}
    </View>
  );
};

const ProductPrices: React.VFC<
  Pick<
    ProductProps,
    | 'show_pricing'
    | 'show_list_price'
    | 'list_price'
    | 'show_net_price'
    | 'net_price'
    | 'net_price_label'
    | 'show_ext_price'
    | 'extended_price'
    | 'currency_type'
  > & { style?: Style | Style[] }
> = ({
  show_pricing,
  show_list_price,
  list_price,
  show_net_price,
  net_price,
  net_price_label,
  show_ext_price,
  extended_price,
  currency_type,
  style,
}) => {
  if (!show_pricing || (!show_list_price && !show_net_price && !show_ext_price)) {
    return null;
  }

  return (
    <View
      style={[
        { backgroundColor: '#fafafa', paddingTop: 2.5, flexShrink: 0 },
        ...(Array.isArray(style) ? style : typeof style !== 'undefined' ? [style] : []),
      ]}
    >
      {show_list_price && (
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            paddingVertical: 2.5,
            paddingHorizontal: 5,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Text
            style={{
              fontFamily: 'Gotham Rounded',
              fontSize: 5,
              color: '#666666',
              opacity: 0.6,
              textTransform: 'uppercase',
            }}
          >
            List price:
          </Text>
          <View style={{ display: 'flex', flexDirection: 'row' }}>
            <Text
              style={{
                fontFamily: 'Gotham Rounded',
                fontWeight: 700,
                fontSize: 5,
                color: '#000000',
                opacity: 0.8,
                paddingRight: 1,
                lineHeight: 1,
              }}
            >
              $
            </Text>
            <Text
              style={{
                fontFamily: 'Gotham Rounded',
                fontWeight: 700,
                fontSize: 9,
                color: '#000000',
                opacity: 0.8,
                lineHeight: 1,
              }}
            >
              {getDisplayPrice({ value: list_price, hideCurrencySign: true })}
            </Text>
            <Text
              style={{
                fontFamily: 'Gotham Rounded',
                fontWeight: 700,
                fontSize: 5,
                color: '#000000',
                opacity: 0.8,
                alignSelf: 'flex-end',
                lineHeight: 1,
                paddingLeft: 1,
                paddingBottom: 1,
              }}
            >
              {currency_type === CurrencyCode.Ca ? 'CAD' : 'USD'}
            </Text>
          </View>
        </View>
      )}
      {show_net_price && (
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            paddingVertical: 2.5,
            paddingHorizontal: 5,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Text
            style={{
              fontFamily: 'Gotham Rounded',
              fontSize: 5,
              color: '#666666',
              opacity: 0.6,
              textTransform: 'uppercase',
            }}
          >
            {net_price_label}:
          </Text>
          <View style={{ display: 'flex', flexDirection: 'row' }}>
            <Text
              style={{
                fontFamily: 'Gotham Rounded',
                fontWeight: 700,
                fontSize: 5,
                color: '#000000',
                opacity: 0.8,
                paddingRight: 1,
                lineHeight: 1,
              }}
            >
              $
            </Text>
            <Text
              style={{
                fontFamily: 'Gotham Rounded',
                fontWeight: 700,
                fontSize: 9,
                color: '#000000',
                opacity: 0.8,
                lineHeight: 1,
              }}
            >
              {getDisplayPrice({ value: net_price, hideCurrencySign: true })}
            </Text>
            <Text
              style={{
                fontFamily: 'Gotham Rounded',
                fontWeight: 700,
                fontSize: 5,
                color: '#000000',
                opacity: 0.8,
                alignSelf: 'flex-end',
                lineHeight: 1,
                paddingLeft: 1,
                paddingBottom: 1,
              }}
            >
              {currency_type === CurrencyCode.Ca ? 'CAD' : 'USD'}
            </Text>
          </View>
        </View>
      )}
      {show_ext_price && (
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            paddingVertical: 2.5,
            paddingHorizontal: 5,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Text
            style={{
              fontFamily: 'Gotham Rounded',
              fontSize: 5,
              color: '#297FCB',
              textTransform: 'uppercase',
            }}
          >
            Extended price:
          </Text>
          <View style={{ display: 'flex', flexDirection: 'row' }}>
            <Text
              style={{
                fontFamily: 'Gotham Rounded',
                fontWeight: 700,
                fontSize: 5,
                color: '#297FCB',
                lineHeight: 1,
                paddingRight: 1,
              }}
            >
              $
            </Text>
            <Text
              style={{
                fontFamily: 'Gotham Rounded',
                fontWeight: 700,
                fontSize: 11,
                color: '#297FCB',
                lineHeight: 1,
              }}
            >
              {getDisplayPrice({ value: extended_price, hideCurrencySign: true })}
            </Text>
            <Text
              style={{
                fontFamily: 'Gotham Rounded',
                fontWeight: 700,
                fontSize: 5,
                color: '#297FCB',
                alignSelf: 'flex-end',
                lineHeight: 1,
                paddingLeft: 1,
                paddingBottom: 1.5,
              }}
            >
              {currency_type === CurrencyCode.Ca ? 'CAD' : 'USD'}
            </Text>
          </View>
        </View>
      )}
    </View>
  );
};

const ProductFinishes: React.VFC<Pick<ProductProps, 'finishes' | 'show_finishes'> & { style?: Style | Style[] }> = ({
  show_finishes,
  finishes,
  style,
}) => {
  if (!show_finishes || !finishes.length) {
    return null;
  }

  return (
    <View
      style={[
        {
          display: 'flex',
          flexDirection: 'row',
          padding: 5,
          justifyContent: 'space-between',
          flexShrink: 0,
        },
        ...(Array.isArray(style) ? style : typeof style !== 'undefined' ? [style] : []),
      ]}
    >
      <View style={{ paddingTop: 10 }}>
        <Text
          style={{
            fontFamily: 'Gotham Rounded',
            fontSize: 5,
            color: '#666666',
            opacity: 0.6,
            textTransform: 'uppercase',
            flexShrink: 0,
          }}
        >
          Finishes:
        </Text>
      </View>
      <View
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          paddingTop: 4,
          paddingLeft: 10,
        }}
      >
        {finishes.map((finish, index) => (
          <View
            style={{
              transform: 'rotate(-45deg)',
              padding: 1,
              borderColor: finish.is_selected ? '#333333' : '#ffffff',
              borderStyle: 'solid',
              borderWidth: 1,
              width: 17,
              height: 9,
              borderRadius: 6,
              marginLeft: index === 0 ? 0 : -2,
              marginTop: 6,
            }}
            key={index}
          >
            <Image src={finish.image} style={{ width: '100%', height: '100%', borderRadius: 6, objectFit: 'cover' }} />
          </View>
        ))}
      </View>
    </View>
  );
};

const ProductDocumentsAndDownloads: React.VFC<
  Pick<
    ProductProps,
    | 'show_documents'
    | 'installation_guide'
    | 'product_sheet'
    | 'customer_service'
    | 'warranty'
    | 'spec_sheet'
    | 'parts_diagram'
  > & {
    condensed?: boolean;
    prominent?: boolean;
    style?: Style | Style[];
  }
> = ({
  show_documents,
  installation_guide,
  product_sheet,
  customer_service,
  warranty,
  spec_sheet,
  parts_diagram,
  condensed,
  prominent,
  style,
}) => {
  if (
    !show_documents ||
    (!installation_guide && !product_sheet && !customer_service && !warranty && !spec_sheet && !parts_diagram)
  ) {
    return null;
  }

  return (
    <View
      style={[
        { flexShrink: 0, minWidth: 90, padding: 4, border: '1px solid #EEEEEE', borderRadius: 5 },
        condensed ? { display: 'flex', alignItems: 'flex-start' } : {},
        ...(Array.isArray(style) ? style : typeof style !== 'undefined' ? [style] : []),
      ]}
    >
      <Text
        style={{
          fontFamily: 'Gotham Rounded',
          fontSize: 5,
          backgroundColor: '#FFFFFF',
          color: '#A5A5A6',
          opacity: 1,
          textTransform: 'uppercase',
          paddingHorizontal: condensed ? 2 : 5,
          width: 'auto',
          position: 'absolute',
          top: -2.5,
          left: 2,
        }}
      >
        Download documents
        <Svg width="8" height="8" viewBox="0 0 16 16" style={{ marginLeft: 2 }}>
          <Path
            d="M12.975 6.85313L8.40357 10.8531C8.18214 11.0469 7.81786 11.0469 7.59643 10.8531L3.025 6.85313C2.80357 6.65938 2.80357 6.34062 3.025 6.14687C3.24643 5.95312 3.61071 5.95312 3.83214 6.14687L7.42857 9.29375V0.5C7.42857 0.225 7.68571 0 8 0C8.31429 0 8.57143 0.225 8.57143 0.5V9.29375L12.1679 6.14687C12.3893 5.95312 12.7536 5.95312 12.975 6.14687C13.1964 6.34062 13.1964 6.65938 12.975 6.85313ZM1.14286 10.5V13.5C1.14286 14.3281 1.91071 15 2.85714 15H13.1429C14.0893 15 14.8571 14.3281 14.8571 13.5V10.5C14.8571 10.225 15.1143 10 15.4286 10C15.7429 10 16 10.225 16 10.5V13.5C16 14.8813 14.7214 16 13.1429 16H2.85714C1.27857 16 0 14.8813 0 13.5V10.5C0 10.225 0.257143 10 0.571429 10C0.885714 10 1.14286 10.225 1.14286 10.5Z"
            opacity=".6"
            fill="#000000"
          />
        </Svg>
      </Text>
      <View
        style={{
          display: 'flex',
          flexDirection: 'row',
          backgroundColor: '#f5faf4',
          borderRadius: 2,
          marginTop: 2,
          paddingVertical: 0,
          width: '100%',
        }}
      >
        {typeof installation_guide !== 'undefined' && Boolean(installation_guide) && (
          <Link src={installation_guide} style={{ padding: prominent ? 6 : 4, borderRight: '1px solid #FFFFFF' }}>
            <Svg width={prominent ? 18 : 8} height={prominent ? 18 : 8} viewBox="0 0 16 16">
              <Path
                d="M12.795 0c.164 0 .3.118.328.273l.005.06v3.402a.333.333 0 0 1-.661.06l-.005-.06V.667L6.05.666v4.385c0 .164-.118.3-.273.328l-.06.006-4.386-.001v9.95h11.463c.164 0 .3.117.328.273l.005.06c0 .163-.118.3-.273.328l-.06.005H1a.333.333 0 0 1-.328-.273l-.005-.06V5.05a.332.332 0 0 1 .061-.193l.002-.002.034-.04L5.482.098a.345.345 0 0 1 .02-.019l-.02.019a.333.333 0 0 1 .183-.094L5.718 0h7.077Zm1.012 5.334c.438.005.852.161 1.192.436a.283.283 0 0 1 .024.418l-.056.044-1.073.645.003.017c.04.208.15.395.31.53l.104.075.017.01.962-.578a.282.282 0 0 1 .177-.038l.06.013.056.027.041.029c.059.049.096.12.1.199.066 1.116-.765 1.96-1.89 1.96l-.137-.006-.138-.017-.112-.022-.845.878-.032.043-.037.04-.091.09 2.034 2.034h.225a.67.67 0 0 1 .558.298l.042.073.67 1.338a.283.283 0 0 1-.02.286l-.034.04-.388.388a.283.283 0 0 1-.278.071l-.048-.018-1.338-.67a.67.67 0 0 1-.365-.515l-.005-.084v-.224l-2.034-2.035-.09.09a.56.56 0 0 1-.016.016l-.52.519a.925.925 0 0 0-.276.534l-.004.113c.02.471-.165.924-.602 1.392-.361.388-.846.622-1.345.622a1.934 1.934 0 0 1-1.121-.363.283.283 0 0 1-.04-.427l.058-.045 1.18-.708-.002-.018a.914.914 0 0 0-.311-.53l-.103-.075-.017-.01-1.136.682a.283.283 0 0 1-.39-.098l-.025-.06-.01-.049-.02-.154a.954.954 0 0 1-.004-.09c0-1.016.736-1.769 1.742-1.838l.153-.005.103-.004a.953.953 0 0 0 .49-.168l.094-.08.292-.316a2 2 0 0 1-.364-.265l-.119-.113-2.037-2.037a1.209 1.209 0 0 1-.091-1.609l.09-.102.227-.226a1.21 1.21 0 0 1 1.608-.09l.102.09 2.038 2.038c.153.153.276.306.368.466l.198-.199a.921.921 0 0 0 .275-.531l.004-.113c-.02-.474.167-.93.61-1.402.364-.388.855-.619 1.357-.612Zm-1.764 5.192-.182.183 2.116 2.117c.04.04.066.09.077.144l.006.055v.343c0 .03.012.058.034.078l.024.016 1.156.579.1-.1-.578-1.157a.105.105 0 0 0-.065-.054l-.03-.004h-.342a.283.283 0 0 1-.157-.047l-.043-.036-2.116-2.117Zm-1.994-.176-.038.033-.006-.015-.348.378-.095.088a1.486 1.486 0 0 1-.84.332l-.161.006-.112.004c-.537.036-.952.333-1.127.78l-.042.125-.016.073.833-.5a.282.282 0 0 1 .144-.041l.074.01.054.02.078.039c.5.25.817.762.817 1.322 0 .08-.034.154-.09.207l-.048.035-.885.532.086.021.08.015.08.01.122.006c.333 0 .671-.163.931-.442.295-.317.429-.593.449-.866l.002-.234c.015-.3.137-.593.338-.836l.106-.117.148-.149-.091-.095a1.89 1.89 0 0 1-.34-.5l-.052-.134a.482.482 0 0 0-.051-.107ZM8.103 5.898a.645.645 0 0 0-.38.123l-.077.066-.226.226a.644.644 0 0 0-.066.834l.066.077 2.038 2.038c.144.144.278.247.413.31l.1.042.092.035c.231.102.418.285.526.512l.047.117.033.084c.048.105.12.21.215.32l.1.106 1.137-1.137-.084-.08a1.562 1.562 0 0 0-.23-.175l-.111-.06-.176-.069a1.053 1.053 0 0 1-.526-.512l-.047-.117-.033-.084a1.308 1.308 0 0 0-.215-.319l-.104-.11-2.037-2.038a.644.644 0 0 0-.455-.189Zm5.697 0c-.336-.004-.678.157-.939.435-.3.32-.435.597-.455.873l-.002.118v.117c-.015.3-.137.59-.337.834l-.106.117-.273.273.006.003-.035.03c.04.03.084.053.132.069.211.07.402.189.592.355l.124.115.647-.672a.282.282 0 0 1 .17-.085h.064l.05.01c.147.044.273.066.396.066.554 0 .995-.272 1.201-.698l.05-.12.011-.031-.613.367a.282.282 0 0 1-.144.04l-.075-.009-.053-.02-.078-.04c-.5-.25-.817-.761-.817-1.32 0-.08.033-.155.09-.208l.047-.035.817-.49-.024-.01a1.333 1.333 0 0 0-.218-.06l-.113-.017-.115-.006Zm-8.416-4.76-3.58 3.58h3.58v-3.58Z"
                fillRule="evenodd"
                opacity=".6"
                fill="#000000"
              />
            </Svg>
          </Link>
        )}
        {typeof product_sheet !== 'undefined' && Boolean(product_sheet) && (
          <Link src={product_sheet} style={{ padding: prominent ? 6 : 4, borderRight: '1px solid #FFFFFF' }}>
            <Svg width={prominent ? 18 : 7} height={prominent ? 18 : 8} viewBox="0 0 23 24">
              <Path
                d="M18.192 0a.5.5 0 0 1 .492.41l.008.09v5.103a.5.5 0 0 1-.992.09l-.008-.09V1H8.076v6.577a.5.5 0 0 1-.41.492l-.09.008-6.578-.001L.999 23h17.193a.5.5 0 0 1 .492.41l.008.09a.5.5 0 0 1-.41.492l-.09.008H.5a.5.5 0 0 1-.492-.41L0 23.5V7.577l.002-.043v-.006a.612.612 0 0 1 .026-.117l.013-.032a.552.552 0 0 1 .02-.041l.022-.036a.596.596 0 0 1 .012-.019l.009-.011a.503.503 0 0 1 .03-.036L7.237.135a.503.503 0 0 1 .036-.031l-.049.042a.5.5 0 0 1 .079-.063L7.338.06a.536.536 0 0 1 .149-.053l.011-.002L7.577 0h10.615ZM8.583 19c.23 0 .417.224.417.5s-.187.5-.417.5H4.417c-.23 0-.417-.224-.417-.5s.187-.5.417-.5Zm7.037-6.501a.447.447 0 0 1 .323.147l.057.076 2.328 3.783 1.03-2.229.043-.076a.455.455 0 0 1 .352-.2.443.443 0 0 1 .365.17l.048.073 2.77 5 .04.096a.536.536 0 0 1-.035.407.465.465 0 0 1-.32.246L22.54 20H11.462l-.084-.008a.473.473 0 0 1-.325-.26.54.54 0 0 1-.013-.437l.042-.08 4.154-6.5.06-.076a.43.43 0 0 1 .324-.14Zm-.011 1.391L12.343 19h9.381l-1.917-3.461-1.009 2.186-.043.075a.453.453 0 0 1-.34.2.431.431 0 0 1-.363-.153l-.05-.069-2.393-3.888ZM8.583 15c.23 0 .417.224.417.5s-.187.5-.417.5H4.417c-.23 0-.417-.224-.417-.5s.187-.5.417-.5ZM20 10c1.103 0 2 .897 2 2s-.897 2-2 2-2-.897-2-2 .897-2 2-2Zm0 1a1.001 1.001 0 0 0 0 2 1.001 1.001 0 0 0 0-2Zm-9.583 0c.322 0 .583.224.583.5s-.261.5-.583.5H4.583C4.261 12 4 11.776 4 11.5s.261-.5.583-.5ZM7.076 1.706l-5.371 5.37h5.371v-5.37Z"
                fillRule="evenodd"
                opacity=".6"
                fill="#000000"
              />
            </Svg>
          </Link>
        )}
        {typeof customer_service !== 'undefined' && Boolean(customer_service) && (
          <Link src={customer_service} style={{ padding: prominent ? 6 : 4, borderRight: '1px solid #FFFFFF' }}>
            <Svg width={prominent ? 18 : 8} height={prominent ? 18 : 8} viewBox="0 0 16 16">
              <Path
                d="M.353 8.011c.687.041 2.972.216 3.543.758a.333.333 0 0 1 .103.27 6.86 6.86 0 0 1-.042.362l5.782 1.284c.986.246 1.481.736 1.577 1.569l2.157-.54a2.179 2.179 0 0 1 2.492 1.147.334.334 0 0 1-.16.452L12.14 14.98c-1.74.714-2.532 1.01-3.208 1.01-.435 0-.822-.123-1.382-.335l-4.777-1.59c-.068.218-.113.355-.122.382a.334.334 0 0 1-.317.23h-2A.333.333 0 0 1 0 14.344v-6c0-.092.038-.18.105-.243a.348.348 0 0 1 .248-.09Zm3.491 2.048c-.218 1.114-.602 2.462-.875 3.369l4.803 1.6c1.202.453 1.299.49 4.101-.659l3.305-1.502a1.516 1.516 0 0 0-1.543-.504l-2.545.635-.01.003-.897.224a2.332 2.332 0 0 1-1.022.024l-2.893-.578a.333.333 0 1 1 .13-.654l2.893.579c.241.049.494.042.73-.017l.64-.16c-.04-.609-.337-.9-1.076-1.084l-5.74-1.276ZM.667 8.7v5.31H2.09c.248-.783 1.047-3.377 1.224-4.837-.42-.204-1.576-.388-2.647-.473ZM9.333.011c.736 0 1.334.598 1.334 1.333v.15a6.01 6.01 0 0 1 4.666 5.85h.334a.333.333 0 0 1 0 .667l-.529-.001-1.235 1.237a.336.336 0 0 1-.236.097H5a.336.336 0 0 1-.236-.097L3.527 8.01H3a.333.333 0 0 1 0-.666h.333A6.011 6.011 0 0 1 8 1.494v-.15C8 .609 8.598.011 9.333.011Zm4.863 7.999H4.47l.668.668h8.39l.668-.668Zm-4.863-6A5.34 5.34 0 0 0 4 7.345h10.667a5.34 5.34 0 0 0-5.334-5.333Zm0 .667a.333.333 0 0 1 0 .667 3.987 3.987 0 0 0-3.892 3.077.333.333 0 1 1-.649-.154 4.65 4.65 0 0 1 4.541-3.59Zm0-2c-.367 0-.666.3-.666.667v.037a6.037 6.037 0 0 1 1.334 0L10 1.344c0-.367-.3-.667-.667-.667Z"
                fillRule="evenodd"
                opacity=".6"
                fill="#000000"
              />
            </Svg>
          </Link>
        )}
        {typeof warranty !== 'undefined' && Boolean(warranty) && (
          <Link src={warranty} style={{ padding: prominent ? 6 : 4, borderRight: '1px solid #FFFFFF' }}>
            <Svg width={prominent ? 18 : 7} height={prominent ? 18 : 8} viewBox="0 0 14 16">
              <Path
                d="m7.032 0 .063.012 6.667 2a.332.332 0 0 1 .23.248l.007.072v7.333c0 2.948-4.494 5.57-6.901 6.319a.32.32 0 0 1-.197 0C4.551 15.252.208 12.734.007 9.87L0 9.665V2.332c0-.123.067-.234.17-.292l.067-.028 6.667-2L6.968 0h.064ZM7 3.346l-6.333 1.9v4.42c0 2.34 3.708 4.801 6.333 5.65 2.556-.827 6.139-3.182 6.326-5.466l.007-.185V5.246L7 3.346Zm3.59 3.059a.367.367 0 0 1 .047.467l-.042.051-3.913 4a.367.367 0 0 1-.47.045l-.052-.043L4.073 8.82a.367.367 0 0 1 .47-.56l.05.043 1.826 1.84 3.652-3.733a.367.367 0 0 1 .519-.005ZM7 .68.667 2.58v1.97l6.238-1.87c.041-.013.085-.017.127-.013l.064.012 6.237 1.871V2.58L7 .68Z"
                fillRule="evenodd"
                opacity=".6"
                fill="#000000"
              />
            </Svg>
          </Link>
        )}
        {typeof spec_sheet !== 'undefined' && Boolean(spec_sheet) && (
          <Link src={spec_sheet} style={{ padding: prominent ? 6 : 4, borderRight: '1px solid #FFFFFF' }}>
            <Svg width={prominent ? 18 : 7} height={prominent ? 18 : 8} viewBox="0 0 14 16">
              <Path
                d="M12.462 0c.163 0 .3.118.328.273l.005.06v3.402a.333.333 0 0 1-.661.06l-.006-.06V.667L5.718.666v4.385c0 .164-.118.3-.273.328l-.06.006-4.387-.001.001 9.95h11.463c.163 0 .3.117.328.273l.005.06c0 .163-.118.3-.274.328l-.06.005H.668a.333.333 0 0 1-.328-.273l-.006-.06V5.05a.332.332 0 0 1 .062-.193l.002-.002.034-.04L5.149.098a.345.345 0 0 1 .02-.019l-.02.019a.333.333 0 0 1 .183-.094L5.385 0h7.077ZM12 10.667a.333.333 0 0 1 0 .666h-1.667V12a.334.334 0 0 1 .569-.235l.451.452 1.766-1.472a.334.334 0 0 1 .427.513l-2 1.666a.334.334 0 0 1-.449-.02l-.666-.667a.33.33 0 0 1-.098-.235v1.332h2v-.666a.333.333 0 0 1 .667 0v1c0 .184-.15.333-.333.333H10a.333.333 0 0 1-.333-.333V11c0-.184.149-.333.333-.333ZM7.333 12a.333.333 0 0 1 0 .667H4A.333.333 0 0 1 4 12Zm5.334-6c.16 0 .295.114.326.266l.007.067V9a.334.334 0 0 1-.266.327l-.067.006H10a.334.334 0 0 1-.327-.266L9.667 9V6.333c0-.16.114-.295.266-.326L10 6h2.667Zm-.334.667h-2v2h2v-2Zm-5 .666a.333.333 0 0 1 0 .667H4a.333.333 0 0 1 0-.667ZM5.05 1.137l-3.58 3.58h3.58v-3.58Z"
                fillRule="evenodd"
                opacity=".6"
                fill="#000000"
              />
            </Svg>
          </Link>
        )}
        {typeof parts_diagram !== 'undefined' && Boolean(parts_diagram) && (
          <Link src={parts_diagram} style={{ padding: prominent ? 6 : 4, borderRight: '1px solid #FFFFFF' }}>
            <Svg width={prominent ? 18 : 7} height={prominent ? 18 : 8} viewBox="0 0 14 16">
              <Path
                d="M12.462 0c.163 0 .3.118.328.273l.005.06v3.402a.333.333 0 0 1-.661.06l-.006-.06V.667L5.718.666v4.385c0 .164-.118.3-.273.328l-.06.006-4.387-.001.001 9.95h11.463c.163 0 .3.117.328.273l.005.06c0 .163-.118.3-.274.328l-.06.005H.668a.333.333 0 0 1-.328-.273l-.006-.06V5.05a.332.332 0 0 1 .062-.193l.002-.002.034-.04L5.149.098a.345.345 0 0 1 .02-.019l-.02.019a.333.333 0 0 1 .183-.094L5.385 0h7.077ZM12 10.667a.333.333 0 0 1 0 .666h-1.667V12a.334.334 0 0 1 .569-.235l.451.452 1.766-1.472a.334.334 0 0 1 .427.513l-2 1.666a.334.334 0 0 1-.449-.02l-.666-.667a.33.33 0 0 1-.098-.235v1.332h2v-.666a.333.333 0 0 1 .667 0v1c0 .184-.15.333-.333.333H10a.333.333 0 0 1-.333-.333V11c0-.184.149-.333.333-.333ZM7.333 12a.333.333 0 0 1 0 .667H4A.333.333 0 0 1 4 12Zm5.334-6c.16 0 .295.114.326.266l.007.067V9a.334.334 0 0 1-.266.327l-.067.006H10a.334.334 0 0 1-.327-.266L9.667 9V6.333c0-.16.114-.295.266-.326L10 6h2.667Zm-.334.667h-2v2h2v-2Zm-5 .666a.333.333 0 0 1 0 .667H4a.333.333 0 0 1 0-.667ZM5.05 1.137l-3.58 3.58h3.58v-3.58Z"
                fillRule="evenodd"
                opacity=".6"
                fill="#000000"
              />
            </Svg>
          </Link>
        )}
      </View>
    </View>
  );
};

const ProductCerts: React.VFC<
  Pick<
    ProductProps,
    | 'show_special_attributes'
    | 'ada_compliant'
    | 'buy_american_act'
    | 'watersense_certified'
    | 'california_compliant'
    | 'lead_free'
    | 'csa_cert'
    | 'upc_cert'
  > & { style?: Style | Style[] }
> = ({
  show_special_attributes,
  ada_compliant,
  buy_american_act,
  watersense_certified,
  california_compliant,
  lead_free,
  csa_cert,
  upc_cert,
  style,
}) => {
  if (
    !show_special_attributes ||
    (!ada_compliant &&
      !buy_american_act &&
      !watersense_certified &&
      !california_compliant &&
      !lead_free &&
      !csa_cert &&
      !upc_cert)
  ) {
    return null;
  }

  return (
    <View
      style={[
        {
          display: 'flex',
          flexDirection: 'row',
          flexShrink: 0,
          flexGrow: 0,
        },
        ...(Array.isArray(style) ? style : typeof style !== 'undefined' ? [style] : []),
      ]}
    >
      {ada_compliant && (
        <View style={{ width: 20, paddingRight: 5, paddingTop: 5, flexGrow: 0, flexShrink: 1 }}>
          <Svg viewBox="0 0 100 100">
            <Path d="M 100.0,50.0 A 50.0,50.0 0 1 1 0.0,50.0 A 50.0,50.0 0 1 1 100.0,50.0" fill="#fff" />
            <Path
              d="M50,0c27.61,0,50,22.39,50,50s-22.39,50-50,50S0,77.61,0,50,22.39,0,50,0ZM31.93,41.21c-5.82,1.43-14.37,7.01-15.08,19.36-.12.71-.12,1.31-.12,2.02,0,7.36,3.32,14.13,8.55,18.65,4.27,3.68,9.85,5.94,15.91,5.94,7.96,0,14.26-2.62,18.88-5.94,2.73-2.02,4.87-4.4,6.54-6.54l-2.38-6.29c-3.68,6.65-9.62,11.4-17.34,12.82-1.78.35-3.68.59-5.58.59-1.42,0-2.97-.24-4.39-.59-7.96-2.02-14.38-9.62-14.38-18.41,0-.71,0-1.54.12-2.25.71-8.67,5.7-12.71,9.39-14.13l-.12-5.22ZM40.12,11.64c-4.99,0-7.36,3.32-7.36,6.53,0,2.38,1.54,3.92,1.66,6.29l1.07,31.48h27.9l8.31,24.11,14.25-4.99-1.42-5.34-8.67,2.96-7.72-21.97h-26.12l-.23-5.58h18.53l-.24-5.22h-18.52l-.36-13.78c2.74,0,6.89-2.85,6.89-7.13,0-5.11-3.92-7.36-7.95-7.36Z"
              fill="#121212"
              fillRule="evenodd"
            />
          </Svg>
        </View>
      )}
      {buy_american_act && (
        <View style={{ width: 20, paddingRight: 5, paddingTop: 5, flexGrow: 0, flexShrink: 1 }}>
          <Svg viewBox="0 0 100 100">
            <Path d="M 100.0,50.0 A 50.0,50.0 0 1 1 0.0,50.0 A 50.0,50.0 0 1 1 100.0,50.0" fill="#fff" />
            <Path
              d="M87.59,82.15c-4.44,5.19-9.93,9.46-16.15,12.45H28.57c-6.22-3-11.71-7.26-16.15-12.45h75.17Z"
              fill="#666"
            />
            <Path d="M98.94,57.24c-.64,4.36-1.85,8.54-3.55,12.46h-49.05v-12.46h52.6Z" fill="#666" />
            <Path d="M96.21,32.33c1.51,3.93,2.53,8.1,2.98,12.45h-52.86v-12.45h49.88Z" fill="#666" />
            <Path d="M75.2,7.43c5.44,3.23,10.2,7.46,14.04,12.45h-42.9V7.43h28.86Z" fill="#666" />
            <Path
              d="M47.16.61c-9.92.56-19.06,4.05-26.57,9.61l-.76.58c-3.54,2.73-6.7,5.92-9.39,9.49l-.06.08c-2.83,3.77-5.13,7.97-6.79,12.47l3.12-.02,1.07-3.17,1.05,3.17,3.35.03-2.69,2,1,3.2-2.72-1.95-2.73,1.95,1-3.2-2.51-1.87c-1.02,2.78-1.78,5.67-2.28,8.66l-.06.33c-.43,2.61-.66,5.29-.66,8.03,0,3.15.31,6.23.87,9.22l.03.15c.69,3.59,1.77,7.05,3.19,10.32h42.54V.61h0ZM10.49,55.63l-2.72-1.95-2.73,1.95,1-3.19-2.69-2,3.35-.03,1.07-3.17,1.05,3.17,3.35.03-2.69,2,1,3.19h0ZM21.04,64.42l-2.72-1.95-2.73,1.95,1.01-3.19-2.69-2,3.35-.02,1.07-3.16,1.05,3.16,3.35.02-2.69,2,1,3.19ZM20.04,43.64l1,3.2-2.72-1.95-2.73,1.95,1.01-3.2-2.69-1.99,3.35-.03,1.07-3.17,1.05,3.17,3.35.03-2.69,1.99ZM20.04,26.06l1,3.19-2.72-1.95-2.73,1.95,1.01-3.19-2.69-2,3.35-.03,1.07-3.17,1.05,3.17,3.35.03-2.69,2ZM31.59,55.63l-2.72-1.95-2.73,1.95,1-3.19-2.69-2,3.35-.03,1.07-3.17,1.05,3.17,3.35.03-2.69,2,1.01,3.19ZM30.58,34.85l1.01,3.2-2.72-1.95-2.73,1.95,1-3.2-2.69-2,3.35-.03,1.07-3.17,1.05,3.17,3.35.03-2.69,2ZM30.58,17.27l1.01,3.19-2.72-1.95-2.73,1.95,1-3.19-2.69-2,3.35-.03,1.07-3.17,1.05,3.17,3.35.03-2.69,2ZM42.14,64.42l-2.72-1.95-2.73,1.95,1.01-3.19-2.69-2,3.35-.02,1.07-3.16,1.06,3.16,3.35.02-2.69,2,1.01,3.19h-.01ZM41.13,43.64l1.01,3.2-2.72-1.95-2.73,1.95,1.01-3.2-2.69-1.99,3.35-.03,1.07-3.17,1.06,3.17,3.35.03-2.69,1.99h-.01ZM41.13,26.06l1.01,3.19-2.72-1.95-2.73,1.95,1.01-3.19-2.69-2,3.35-.03,1.07-3.17,1.06,3.17,3.35.03-2.69,2h-.01ZM41.13,8.48l1.01,3.2-2.72-1.95-2.73,1.95,1.01-3.2-2.69-2,3.35-.03,1.07-3.17,1.06,3.17,3.35.03-2.69,2h-.01Z"
              fill="#121212"
            />
            <Path
              d="M97.86,49.77c0-26.12-19.7-46.67-45.52-47.57l-1.23-.03C27.61,1.92,1.48,20.71,2.15,49.06l.02.67c1.01,26.59,21.47,48.1,47.86,48.1,26.42,0,47.83-21.53,47.83-48.06ZM100,49.77c0,27.75-22.38,50.23-49.97,50.23C22.41,100,1.08,77.48.04,49.81l-.02-.71C-.7,19.21,26.77-.26,51.13,0c27.59.29,48.87,22.03,48.87,49.77Z"
              fill="#121212"
            />
          </Svg>
        </View>
      )}
      {watersense_certified && (
        <View style={{ width: 20, paddingRight: 5, paddingTop: 5, flexGrow: 0, flexShrink: 1 }}>
          <Svg viewBox="0 0 100 100">
            <Path d="M 100.0,50.0 A 50.0,50.0 0 1 1 0.0,50.0 A 50.0,50.0 0 1 1 100.0,50.0" fill="#fff" />
            <Path
              d="M97.86,49.77c0-26.12-19.7-46.67-45.52-47.57l-1.23-.03C27.61,1.92,1.48,20.71,2.15,49.06l.02.67c1.01,26.59,21.47,48.1,47.86,48.1,26.42,0,47.83-21.53,47.83-48.06ZM100,49.77c0,27.75-22.38,50.23-49.97,50.23C22.41,100,1.08,77.48.04,49.81l-.02-.71C-.7,19.21,26.77-.26,51.13,0c27.59.29,48.87,22.03,48.87,49.77Z"
              fill="#121212"
            />
            <Path
              d="M14.15,25.28l10.51,7.34.09.06-.04.1-.44,1.04h-.1s-11-1.21-11-1.21l8.54,6.96.08.06-.04.09-.57,1.32-.11-.02-10.25-2.13v.02l-.16-.03c-.73-.15-1.08-.18-1.35-.06-.13.06-.26.16-.4.33-.15.17-.31.39-.51.68l-.06.09-.1-.04-.29-.12-.13-.05.05-.13,1.21-2.8.04-.1.11.02,9.57,1.98-7.74-6.3-.08-.06.65-1.52.04-.09h.1s9.54,1.05,9.54,1.05l-7.68-5.36-.09-.06.04-.1.39-.89.07-.15.13.09ZM24.24,20.52c.72.06,1.38.52,2,1.1l4.21,3.89.1.09-.09.1-.95,1.04-.09.1-.72-.66c.05.68.03,1.68-.83,2.61-.47.51-1.04.78-1.62.8-.58.02-1.16-.2-1.65-.64h0c-.51-.47-.73-1-.77-1.56-.03-.55.12-1.1.35-1.62.44-.96,1.17-1.82,1.58-2.3l-.43-.39c-.48-.44-.94-.86-1.4-1.05-.23-.09-.45-.12-.67-.07-.16.04-.34.13-.52.28l-.18.18c-.44.48-.47.9-.37,1.23.11.35.36.63.52.8l.08.09-.08.09-1.11,1.19-.1.1-.1-.1c-.39-.42-.7-.99-.7-1.7,0-.71.33-1.52,1.17-2.43l.32-.32c.73-.68,1.4-.93,2.04-.88ZM26.38,24.04c-.27.32-.71.87-.97,1.47-.14.34-.23.68-.21,1.01.02.32.16.64.46.92l.18.15c.2.14.43.24.68.27.33.04.68-.06,1-.4.39-.41.44-.94.3-1.46-.13-.52-.45-1.01-.77-1.31l-.69-.64ZM30.07,15.31l1.83-1.11.11-.07.07.11.4.64-.12.07-1.81,1.11,3.1,5.06c.27.44.55.61.84.63.29.02.63-.11,1-.36h0c.2-.11.36-.26.54-.41l.12-.1.08.13.35.57.12.2h-.2c-.25.24-.53.44-.81.62h0c-1.06.65-1.78.84-2.36.63-.29-.1-.53-.3-.76-.56-.22-.26-.43-.58-.66-.95l-2.82-4.6-1.09.67-.12.07-.07-.12-.33-.54-.07-.12.12-.07,1.1-.67-.75-1.22-.03-.05.02-.06.55-1.78.09-.28,1.55,2.51ZM39.09,11.3c.75-.16,1.47-.09,2.1.23.72.37,1.29,1.08,1.6,2.15l.08.27.04.13-.13.04-5.01,1.46.08.24h0c.24.83.83,1.76,1.67,2.4.83.64,1.89.97,3.07.63l.3-.11c.3-.13.59-.3.86-.46l.15-.09.23.75.09.3-.23-.12c-.29.2-1.06.64-2.07.93-1.32.38-2.51.28-3.47-.3-.96-.58-1.69-1.64-2.13-3.13-.37-1.29-.43-2.41-.06-3.3.38-.9,1.2-1.56,2.51-1.94l.32-.08ZM49.97,9.8s.1,0,.16.01l.06.02.08.03v.08s.1,1.52.1,1.52v.18s-.16-.04-.16-.04c-.22-.05-.4-.1-.58-.1-.42.03-.73.15-.97.33-.24.18-.41.44-.54.72-.18.43-.24.94-.24,1.4v.44s.26,4.41.26,4.41v.13s-1.53.08-1.53.08h-.13s0-.12,0-.12l-.38-6.58h-.01v-.13c-.02-.27-.09-.47-.17-.61-.08-.14-.2-.23-.32-.3-.26-.13-.57-.14-.85-.15h-.12s0-.12,0-.12l-.02-.32v-.12s.11-.02.11-.02l2.94-.63.1,1.66c.09-.27.22-.55.4-.81.36-.5.92-.91,1.83-.97h0ZM40.85,13.7c-.27-.94-1.07-1.59-2.04-1.31-.75.22-1.09.68-1.22,1.16-.12.42-.09.85-.05,1.16l3.33-.97v-.03Z"
              fill="#121212"
            />
            <Path
              d="M84.56,33.64c1.05-.35,2-.45,2.81-.19.93.3,1.66,1.05,2.16,2.32l.11.31c.23.73.24,1.46-.03,2.12-.3.75-.95,1.38-2,1.79h0s-.26.09-.26.09l-.12.04-1.98-4.99-.22.09c-.8.32-1.68,1-2.24,1.89-.56.88-.8,1.97-.35,3.11l.13.29c.15.29.35.57.54.81l.11.14-.17.07-.48.19v.25l-.23-.23c-.19-.19-.76-.97-1.18-2.04-.51-1.28-.52-2.47-.02-3.49.5-1.01,1.49-1.83,2.93-2.4l.46-.17ZM87.28,34.8c-.43-.08-.85-.01-1.16.06l1.27,3.23h.03c.91-.37,1.48-1.22,1.13-2.17l-.12-.25c-.3-.55-.73-.78-1.15-.86ZM80.99,25.95c.59-.42,1.24-.62,1.92-.47.67.15,1.33.64,1.98,1.53.55.76.84,1.58.95,1.85l.04.1-.09.06-.87.62-.19.38-.06-.43c-.05-.34-.13-1.34-.7-2.15-.23-.31-.49-.55-.77-.65-.27-.1-.57-.09-.89.13-.32.23-.42.59-.41,1.05.01.48.14,1.03.25,1.63.21,1.17.34,2.51-.9,3.41-.78.56-1.56.67-2.3.43-.73-.24-1.38-.81-1.94-1.58-.71-.99-.95-1.7-1.11-2.18l-.03-.09,1.01-.74.18-.13.04.21c.04.22.1,1.35.63,2.23l.11.17.12.16c.28.35.58.6.89.7.35.11.74.06,1.19-.26l.11-.1c.24-.24.33-.57.34-1,0-.49-.11-1.05-.21-1.65-.1-.59-.19-1.21-.13-1.77.06-.57.29-1.09.82-1.48ZM73.58,16.6l2.59,1.5-.1.12-1.21,1.49c.34-.14.78-.29,1.26-.35.74-.09,1.6,0,2.36.61h0c.71.57,1.02,1.13,1.03,1.69,0,.55-.28,1.08-.7,1.59l-4.1,5.09-.09.11-.1-.09-1.1-.89-.1-.08.08-.1,3.72-4.62.23-.28c.07-.09.14-.19.2-.28.12-.18.21-.35.25-.52.04-.16.04-.32-.03-.49-.07-.16-.2-.34-.44-.54-.23-.19-.54-.28-.91-.29-.36-.01-.77.06-1.17.18-.81.25-1.57.72-1.93,1.16l-2.74,3.41-.08.11-.1-.09-1.1-.9-.1-.09.08-.1,4.26-5.27.08-.1c.11-.15.16-.29.18-.42.03-.15,0-.29-.05-.42-.11-.27-.34-.49-.53-.69l-.08-.09.08-.09.2-.25.07-.09.1.06ZM64.53,13.29c.86-.47,1.91-.47,3.18.05.82.34,1.47.89,1.8,1.62.33.74.34,1.65-.09,2.68l-.1.26-.05.13-.12-.05-4.83-1.98-.09.23h0c-.33.8-.44,1.9-.19,2.92.24.95.8,1.82,1.8,2.31l.21.09h0c.39.17.84.24,1.25.28l.17.02-.06.16-.22.56-.03.09h-.11c-.27,0-1.22-.13-2.28-.57-1.28-.52-2.14-1.33-2.53-2.4-.39-1.06-.29-2.35.3-3.8l.2-.45c.48-1.01,1.07-1.75,1.81-2.16ZM54.85,7.01c.8-.44,1.87-.56,3.09-.38h0c1.19.18,2.16.5,2.67.7l.1.04-.02.11-.18,1.23.21.42-.41-.27c-.49-.32-1.46-.91-2.6-1.13l-.23-.04c-.63-.1-1.18,0-1.58.24-.4.24-.66.62-.73,1.13h0c-.1.66.17,1.19.66,1.69.49.5,1.17.96,1.87,1.46,1.38.99,2.81,2.16,2.5,4.23-.19,1.3-.87,2.14-1.81,2.6-.93.46-2.1.54-3.29.36-1.28-.19-2.57-.69-2.88-.8l-.1-.04.19-1.36.03-.21.18.12c.62.43,1.78,1.06,3,1.24.78.11,1.41.02,1.86-.28.45-.29.76-.79.87-1.54l.02-.26c.02-.6-.24-1.08-.64-1.53-.47-.52-1.13-.97-1.81-1.45-1.34-.96-2.75-2.06-2.45-4.06.15-1.03.68-1.77,1.48-2.21ZM67.09,14.16c-.73-.3-1.27-.15-1.68.13-.35.25-.6.6-.75.87l3.19,1.32v-.02s.04-.08.04-.08h0c.16-.43.19-.88.07-1.27-.12-.41-.41-.76-.88-.95h0Z"
              fill="#666"
              fillRule="evenodd"
            />
            <Path
              d="M12.95,51.67c0,1.01-.81,1.81-1.82,1.81s-1.81-.81-1.81-1.81.81-1.82,1.81-1.82,1.82.82,1.82,1.82Z"
              fill="#121212"
            />
            <Path
              d="M91.02,51.67c0,1.01-.82,1.82-1.81,1.82s-1.82-.82-1.82-1.82.82-1.82,1.82-1.82,1.81.82,1.81,1.82Z"
              fill="#666"
            />
            <Path
              d="M45.51,31.93c-.33,1.27-.63,2.52-.9,3.95-.43,2.26-1.41,7.05.39,11.5.34.85.61,1.52,1.03,2.28,1.78,3.26,4.43,6.15,5.28,7.15,2.57,3.03,4.62,5.25,7,7.89.42.46,1.15,1.19,1.75,1.96.77.97,1.79,2.01,2.38,3.38.02.04.03.07.05.11-3.28,3.24-7.78,5.39-12.57,5.42-11.01.08-18.47-9.08-17.8-18.14.48-6.5,5.71-9.98,13.39-25.51Z"
              fill="#121212"
            />
            <Path
              d="M49.98,22.5c1.87,4.42,5.05,11.77,9.18,18.29,3.74,5.92,7.54,10.28,8.24,14.61.63,3.99.22,8.9-3.03,12.8.25.41.53.85.71,1.11.55.76,2.42,1.55,2.42,1.55,0,.02-.55,1.87-.55,2.47-1.39-.65-2.72-2.74-3.43-4.21-.03.03-.05.06-.08.09l-.08-.25c-.96-2.45-4.41-5.68-8.47-10.37-4.21-4.85-9.24-9.81-9.94-15.89-.7-6.08,2.01-12.98,5.03-20.21Z"
              fill="#666"
            />
            <Path
              d="M29.91,83.05c.58-1.11,1.77-1.13,2.76-.62,1.8.95,1.23,2.34,1.1,2.59l-.82-.43h0c.16-.34.31-.92-.75-1.48-.27-.14-1.04-.43-1.38.19-.22.41,0,.64.56,1.16l.74.66c.92.82,1.08,1.38.71,2.08-.55,1.04-1.73,1.22-2.86.62-2-1.05-1.53-2.28-1.24-2.9l.82.43c-.18.42-.4,1.11.79,1.74.61.32,1.29.36,1.58-.2.21-.41,0-.68-.63-1.24l-.85-.76c-.61-.54-.89-1.13-.53-1.83ZM29.5,78.84l-.91,1.29.75.54-.45.64-.75-.53h0s-2.14,3.03-2.14,3.03c-.27.35.05.57.21.69l.28.2-.43.61c-.32-.18-.55-.29-.63-.35-.77-.55-.63-1.1-.23-1.66l2.17-3.06-.63-.44.46-.64.63.44.91-1.3.77.55ZM20.23,76.93c1.22-1.27,2.79-1.71,4.16-.42,1.19,1.13.88,2.63-.5,4.09l-3.02-2.85c-.82.85-.89,1.74-.09,2.5.65.62,1.52.48,1.83.19l.69.67h.01c-.21.18-.96.58-1.81.43-.3-.06-.73-.15-1.39-.78-1.16-1.1-1.02-2.63.12-3.82ZM23.69,77.05c-.63-.61-1.7-.49-2.3.14l2.29,2.19h.01c.56-.68.86-1.52,0-2.33ZM15.83,71.25c1.41-1.04,3.05-1.18,4.17.33.97,1.32.41,2.75-1.22,3.94l-2.47-3.34c-.96.7-1.18,1.55-.53,2.44.54.72,1.41.74,1.78.51l.56.77h-.01c-.23.13-1.04.4-1.85.1-.29-.11-.69-.28-1.23-1-.96-1.3-.54-2.76.78-3.75ZM19.21,71.97c-.53-.71-1.59-.78-2.3-.27l1.88,2.56c.68-.57,1.12-1.36.42-2.29ZM16.79,61.79l-5.11,4.75v.02l6.94-.69.62,1.37-7.12,3.2-.42-.94h-.01l4.21-1.89c.21-.08,1.12-.48,1.79-.77h-.01l-6.89.67-.42-.96,5.07-4.69v-.02c-.67.29-1.57.72-1.78.82l-4.21,1.89-.42-.92,7.13-3.21.62,1.38Z"
              fill="#121212"
            />
            <Path
              d="M66.62,80.33c1.79-.95,3.3-.33,4.04.75l-.9.48h0c-.9-1.06-2.03-.75-2.69-.39-1.25.65-1.78,2.12-.81,3.93.87,1.64,2.09,2.57,3.81,1.66.61-.33,1.5-1.17.96-2.95l.91-.47c.99,2.81-1.06,4.03-1.58,4.31-1.58.83-3.65.63-5.05-2.03-1.13-2.12-.7-4.23,1.3-5.3ZM73.97,79.37l-.29.21c-.72.51-.77,1.43-.29,2.09l1.94,2.68h-.01s-.77.55-.77.55l-3.34-4.62.74-.54.56.78h.02c-.08-.78.06-1.44.64-1.85.09-.08.15-.09.22-.12l.58.81ZM78.52,81.78l-.72.63-3.71-4.33.72-.62,3.71,4.31ZM76.81,75.56l.64-.64.56.56-.64.64,2.62,2.61c.3.32.57.05.73-.09l.23-.25.54.54c-.22.27-.39.49-.46.56-.68.67-1.18.44-1.67-.06l-2.65-2.64-.55.55-.56-.56.54-.54-1.12-1.12.68-.68,1.12,1.12ZM74.13,76.7l-.72.63h0s-.71-.83-.71-.83l.73-.63.71.83ZM79.37,72.13c1.02-1.29,2.54-1.12,4.13.13l-2.56,3.26c.94.72,1.81.73,2.49-.15.55-.7.34-1.56.02-1.85l.58-.76h0c.2.21.67.91.61,1.77-.02.32-.08.75-.64,1.46-.99,1.26-2.52,1.26-3.81.25-1.39-1.09-1.98-2.62-.82-4.1ZM82.28,72.56c-.72-.49-1.6-.71-2.32.21-.55.69-.33,1.74.36,2.29l1.96-2.49h-.01ZM83.84,67.53l-.18.3c-.46.77-.15,1.61.55,2.03l2.84,1.71h-.01s-.49.83-.49.83l-4.88-2.93.47-.77.81.48h.01c-.36-.68-.49-1.34-.13-1.95.07-.11.11-.14.16-.2l.84.5ZM88.98,67.99l-.42.86-5.1-2.51.42-.87,5.09,2.52ZM88.98,67.99h0s0-.01,0-.01h0ZM85.15,62c.16-.43.84-1.94,2.21-1.45l3.09,1.11c.22.08.35,0,.42-.19.02-.08.05-.21.07-.32l.66.23c-.01.16,0,.33-.07.54-.29.79-.75.77-1.17.65.26.49.55,1.2.17,2.26-.35.98-1.23,1.45-2.15,1.12-.46-.16-1.45-.68-1.09-2.23l.35-1.56c.06-.22.07-.5-.46-.7-.56-.2-1.02.09-1.33.95-.36,1.02.21,1.39.58,1.6l-.3.83c-1.26-.5-1.46-1.53-.98-2.85ZM82.96,65.01l-.42.85-.98-.48.42-.87.98.49ZM88.32,61.92c.06.18.06.43-.2,1.6-.11.47-.21,1.22.46,1.46.58.21,1.04.05,1.29-.63.3-.84.06-1.86-.66-2.12l-.88-.32h-.01Z"
              fill="#121212"
              fillRule="evenodd"
            />
            <Path
              d="M45.93,84.78h-5.2v2.69h4.81v1.06h-4.81v2.93h5.3v1.05h-6.49v-8.79h6.4v1.05ZM51.78,83.72c1.62,0,2.59,1.05,2.59,2.47,0,1.23-.69,2.61-2.59,2.61h-2.77v3.72h-1.19v-8.79h3.96ZM61.89,92.52h-1.3l-.87-2.56h-3.53l-.92,2.56h-1.23l3.31-8.79h1.34l3.19,8.79ZM56.56,88.9h2.74l-1.3-3.87h-.02l-1.42,3.87ZM49.01,87.79h2.36c1.06,0,1.79-.4,1.79-1.58,0-1.11-.76-1.47-1.73-1.47h-2.42v3.05Z"
              fill="#121212"
            />
          </Svg>
        </View>
      )}
      {california_compliant && (
        <View style={{ width: 20, paddingRight: 5, paddingTop: 5, flexGrow: 0, flexShrink: 1 }}>
          <Svg viewBox="0 0 100 100">
            <Path d="M 100.0,50.0 A 50.0,50.0 0 1 1 0.0,50.0 A 50.0,50.0 0 1 1 100.0,50.0" fill="#fff" />
            <Path
              d="M50,0c27.63,0,50,22.4,50,50h-.02c0,27.6-22.37,50-50,50S0,77.6,0,50C0,22.4,22.37,0,50,0ZM33.44,26.32c-2.68,0-5.05.46-7.12,1.38-2.05.9-3.66,2.14-4.84,3.72-1.15,1.58-1.85,3.23-2.08,4.95-.23,1.7-.35,4.26-.35,7.68v13.86c0,4.65.38,8.14,1.14,10.46.76,2.31,2.31,4.11,4.66,5.42,2.34,1.31,5.33,1.96,8.94,1.96,3.48,0,6.4-.77,8.76-2.32,2.38-1.54,3.92-3.34,4.6-5.39.7-2.05,1.05-5.53,1.05-10.43v-1.85h-12.34v5.95c0,2.6-.15,4.25-.44,4.95-.27.7-.87,1.05-1.79,1.05-.96,0-1.57-.35-1.85-1.05-.27-.7-.41-2.21-.41-4.51v-22.04c0-2.4.14-3.97.41-4.69.27-.72.92-1.08,1.93-1.08.9,0,1.47.3,1.73.91.27.59.41,2.08.41,4.48v8.26h12.34v-4.51c0-4.43-.37-7.7-1.11-9.79-.74-2.11-2.3-3.87-4.66-5.28-2.36-1.41-5.36-2.11-9-2.11ZM66.21,26.32c-3.67,0-6.67.86-9,2.58-2.31,1.7-3.7,3.58-4.19,5.63-.49,2.05-.73,5.34-.73,9.88v14.1c0,3.26.12,5.71.35,7.36.23,1.62.82,3.18,1.76,4.66.94,1.48,2.31,2.74,4.1,3.75,1.82,1,3.76,1.49,5.83,1.49,1.74,0,3.24-.36,4.51-1.08,1.29-.74,2.36-1.85,3.2-3.31l1.14,3.4h7.77v-25.47h-14.45v7.21h2.49v5.63c0,2.21-.17,3.69-.5,4.46-.31.76-.92,1.14-1.82,1.14-.82,0-1.37-.33-1.64-1-.27-.68-.41-2.09-.41-4.22v-22.77c0-2.42.14-3.93.41-4.51.27-.59.79-.88,1.55-.88.88,0,1.44.34,1.67,1.03.23.68.35,2.38.35,5.1v4.31h12.34v-2.05c0-3.89-.36-6.85-1.09-8.88-.72-2.05-2.28-3.82-4.66-5.3-2.38-1.48-5.38-2.23-9-2.23Z"
              fill="#121212"
              fillRule="evenodd"
            />
          </Svg>
        </View>
      )}
      {lead_free && (
        <View style={{ width: 20, paddingRight: 5, paddingTop: 5, flexGrow: 0, flexShrink: 1 }}>
          <Image src={leadFreeImage} />
        </View>
      )}
      {csa_cert && (
        <View style={{ width: 20, paddingRight: 5, paddingTop: 5, flexGrow: 0, flexShrink: 1 }}>
          <Svg viewBox="0 0 805.89 737.58">
            <G fill="#231f20">
              <Path d="M641.66 347.47c17.369 0 31.473-14.09 31.473-31.473 0-17.39-14.104-31.458-31.473-31.458-17.377 0-31.47 14.068-31.47 31.458 0 17.382 14.093 31.473 31.47 31.473m-40.12-31.473c0-22.157 17.961-40.116 40.12-40.116 22.162 0 40.123 17.96 40.123 40.116 0 22.165-17.961 40.138-40.123 40.138-22.159 0-40.12-17.973-40.12-40.138m-127.06 91.698a141.89 141.89 0 0 0 29.169-59.659h-48.636l-.116 147.98a198.69 198.69 0 0 1-62.194 19.193v-396.44l.013-.368c97.808 13.178 172.96 97 172.96 198.42 0 49.405-17.905 94.655-47.572 129.58zm-20.037-120.87h49.34c-6.961-32.098-24.725-60.157-49.296-80.173z" />
              <Path d="M643.6 305.14h-10.43v7.11h10.728c1.37-.16 2.899-1.68 2.899-3.767 0-2.078-3.196-3.343-3.196-3.343m-10.43 16.832v16.546h-10.005v-42.143l26.29.14s8.889 2.793 9.31 9.47c.423 6.652-.559 7.374-1.392 8.765-.833 1.382-3.205 4.587-5.554 5.692l9.722 18.076h-11.538l-9.05-16.546zm42.776 295.12v74.227c0 25.506-16.338 32.202-30.89 32.202-14.23 0-29.768-7.025-29.768-32.53v-73.899h-15.856v78.302c0 21.907 13.57 42.19 45.134 42.19 32.205 0 47.24-20.282 47.24-44.311v-76.181zm126.66 34.326c-.663-12.908-4.747-37.442-45.784-37.442-28.443 0-40.37 17.985-40.37 36.299 0 20.441 17.324 25.835 24.999 27.638l24.035 5.559c19.293 4.578 25.022 8.487 25.022 20.766 0 16.832-19.297 19.773-30.743 19.773-15.036 0-32.37-6.205-32.37-27.466h-14.87c0 10.957 1.961 20.758 11.924 29.762 5.238 4.747 13.74 11.274 35.476 11.274 20.107 0 45.942-7.678 45.942-35.475 0-18.636-11.434-27.8-26.633-31.39l-33.524-7.68c-6.87-1.635-13.902-4.91-13.902-14.881 0-17.826 16.844-20.61 26.817-20.61 13.24 0 28.278 5.728 29.1 23.873zm-630.89-283.73c20.279 77.402 85.71 136.54 166.45 147.53v-184.18l-93.84-90.194a143.29 143.29 0 0 1 34.12-37.122l-.087 33.288h60.662V118.33c-79.601 10.5-144.45 67.726-166.1 143.22l104.28 93.387v74.621c-24.783-19.284-43.024-46.592-50.827-78.034z" />
              <Path d="M130.36 317.15c.47 129.74 105.8 234.78 235.65 234.78 72.59 0 137.5-32.831 180.73-84.442l61.016 53.265c-58.16 69.382-145.49 113.5-243.11 113.5-175.13 0-317.11-141.98-317.11-317.1 0-175.14 141.98-317.13 317.11-317.13 98.022 0 185.65 44.474 243.82 114.35l-61.002 51.565c-43.224-52.101-108.45-85.298-181.46-85.298-130.14 0-235.65 105.5-235.65 235.65zm-27.354 333.46c-2.288-19.625-17.997-38.434-48.235-38.434-34.166 0-54.768 25.014-54.768 61.154 0 45.119 26.147 62.453 52.804 62.453 8.821 0 44.802-1.964 51.344-46.272H88.775c-5.392 27.307-23.221 32.217-33.512 32.217-29.113 0-38.916-21.262-38.916-48.888 0-30.903 17.169-46.604 38.424-46.604 11.273 0 28.455 3.764 32.857 24.374z" />
            </G>
          </Svg>
        </View>
      )}
      {upc_cert && (
        <View style={{ width: 20, paddingRight: 5, paddingTop: 5, flexGrow: 0, flexShrink: 1 }}>
          <Svg viewBox="0 0 200 193.71">
            <G fill="#231f20">
              <Path d="M19.864 2.852a1.532 1.532 0 0 0 .765 2.025l166.3 75.327c.778.343 1.688.007 2.028-.761.347-.772 0-1.678-.761-2.025L21.896 2.091a1.543 1.543 0 0 0-2.036.761m.019 55.252a1.523 1.523 0 0 0 .73 2.028l151.15 71.415c.75.365 1.663.032 2.02-.729a1.522 1.522 0 0 0-.722-2.035L21.917 57.37a1.53 1.53 0 0 0-2.035.732" />
              <Path d="M19.312 0c-.85 0-1.522.736-1.53 1.575l-.017 1.183c-.347 27.792.361 53.457 1.972 72.269.018.12.524 5.154.524 5.154l.47 4.726c1.466 14.765 5.66 23.65 8.164 28.95l1.228 2.7c5.023 11.226 14.156 22.529 20.192 29.995l.584.718c7.09 9.087 22.128 23.828 53.383 42.306l1.64.98c.491.288 1.359.337 1.847.026l.974-.61c14.73-9.154 41.393-27.105 54.604-44.705.02-.018 3.242-4.361 3.242-4.361 5.066-6.762 11.377-15.183 15.066-24.348 1.76-4.259 6.018-15.905 7.434-31.743.216-.931.506-4.185.804-7.728l.173-2.043c2.482-31.074 2.227-73.076 1.87-74.056a1.523 1.523 0 0 0-1.434-.987H19.312m169.68 3.048c.028 1.869.063 5.494.063 10.563 0 14.39-.304 39.34-2.039 61.16 0-.025-.184 2.063-.184 2.063-.202 2.429-.545 6.51-.76 7.516-1.406 15.615-5.544 26.91-7.258 31.046-3.54 8.81-9.71 17.056-14.673 23.682l-3.246 4.379c-9.558 12.726-28.667 28.323-53.765 43.91l-.28.212c-.333-.198-.566-.372-1.002-.64-30.865-18.225-45.62-32.66-52.562-41.574a61.9 61.9 0 0 1-.594-.732c-5.944-7.346-14.907-18.443-19.764-29.272.014.042-1.275-2.808-1.275-2.808-2.414-5.122-6.464-13.71-7.89-28.022-.01-.074-.464-4.623-.464-4.623l-.531-5.26c-1.328-15.477-2.032-35.786-2.032-58.126 0-4.414.028-8.917.082-13.473z" />
              <Path d="M67.338 43.212v17.126c0 2.01.39 3.682 1.15 5.01 1.14 2.02 3.07 3.022 5.767 3.022 3.236 0 5.434-1.094 6.602-3.26.627-1.186.935-2.775.935-4.772V43.212h3.76v15.576c0 3.413-.465 6.036-1.381 7.876-1.7 3.342-4.907 5.02-9.619 5.02-4.704 0-7.9-1.678-9.586-5.02-.927-1.84-1.384-4.464-1.384-7.876V43.212zm31.099 15.166h12.397c2.45 0 4.44.697 5.954 2.089 1.515 1.39 2.276 3.356 2.276 5.883 0 2.167-.672 4.053-2.028 5.668-1.363 1.6-3.42 2.407-6.191 2.407h-8.656v11.717h-3.752V58.378M113 62.272c-.828-.39-1.958-.58-3.391-.58h-7.42v9.543h7.42c1.674 0 3.03-.347 4.078-1.066 1.04-.708 1.56-1.964 1.56-3.77 0-2.025-.75-3.402-2.247-4.127m37.5 13.778c1.908 1.855 2.974 3.961 3.186 6.322h-3.7c-.406-1.784-1.213-3.193-2.438-4.237-1.222-1.037-2.928-1.558-5.13-1.558-2.686 0-4.853.953-6.506 2.875-1.657 1.919-2.478 4.857-2.478 8.818 0 3.24.74 5.873 2.234 7.89 1.49 2.019 3.71 3.02 6.676 3.02 2.715 0 4.793-1.065 6.216-3.196.747-1.133 1.313-2.613 1.682-4.44h3.702c-.322 2.925-1.394 5.385-3.217 7.37-2.17 2.387-5.12 3.58-8.815 3.58-3.19 0-5.862-.974-8.025-2.92-2.85-2.581-4.276-6.578-4.276-11.966 0-4.096 1.072-7.451 3.214-10.078 2.315-2.842 5.508-4.266 9.58-4.266 3.465 0 6.176.932 8.095 2.787m44.35 112.059c-3.43 3.448-7.572 5.158-12.432 5.158-4.864 0-9.013-1.71-12.422-5.158-3.398-3.434-5.098-7.575-5.098-12.443 0-4.829 1.714-8.953 5.144-12.376 3.409-3.405 7.533-5.105 12.376-5.105 4.86 0 9.002 1.7 12.432 5.105 3.427 3.409 5.154 7.54 5.154 12.376 0 4.85-1.727 8.995-5.154 12.443m-23.12-23.081c-2.945 2.945-4.404 6.492-4.404 10.652 0 4.202 1.459 7.78 4.372 10.73 2.935 2.956 6.51 4.436 10.708 4.436 4.195 0 7.77-1.48 10.698-4.436 2.938-2.949 4.411-6.528 4.411-10.73 0-4.16-1.473-7.707-4.41-10.652-2.957-2.952-6.514-4.425-10.699-4.425-4.166 0-7.724 1.473-10.677 4.425m10.372.984c2.407 0 4.167.23 5.278.694 2 .828 2.999 2.46 2.999 4.885 0 1.724-.627 2.995-1.887 3.802-.67.439-1.607.747-2.8.942 1.515.24 2.623.863 3.32 1.876.698 1.023 1.045 2 1.045 2.966v1.402c0 .45.02.92.053 1.437.02.496.074.84.159.992l.11.237h-3.147a1.04 1.04 0 0 1-.046-.188c-.01-.067-.032-.127-.046-.212l-.071-.616v-1.512c0-2.202-.609-3.67-1.834-4.375-.725-.41-1.993-.616-3.812-.616h-2.701v7.519h-3.377v-19.233h6.757m3.668 3.129c-.85-.474-2.238-.712-4.138-.712h-2.91v6.882h3.083c1.451 0 2.531-.145 3.26-.432 1.331-.517 2.004-1.497 2.004-2.966 0-1.377-.436-2.298-1.3-2.772M24.256 162.99c2.227 2.145 3.466 4.584 3.71 7.314h-4.273c-.47-2.05-1.416-3.675-2.828-4.871-1.413-1.2-3.395-1.799-5.947-1.799-3.116 0-5.625 1.112-7.544 3.335-1.912 2.223-2.871 5.628-2.871 10.22 0 3.76.864 6.807 2.591 9.147 1.728 2.34 4.308 3.508 7.735 3.508 3.15 0 5.554-1.235 7.2-3.702.875-1.303 1.526-3.013 1.958-5.133h4.273c-.38 3.398-1.622 6.248-3.731 8.545-2.531 2.768-5.944 4.152-10.238 4.152-3.703 0-6.811-1.132-9.325-3.401C1.656 187.31 0 182.683 0 176.42c0-4.75 1.242-8.648 3.73-11.688 2.691-3.307 6.401-4.96 11.13-4.96 4.036 0 7.166 1.073 9.396 3.218" />
            </G>
          </Svg>
        </View>
      )}
    </View>
  );
};

const Products: React.VFC<{
  products: ProductProps[];
}> = ({ products }) => {
  return (
    <View style={{ display: 'flex', width: '100%', height: '100%' }}>
      {products.map((product, index) => (
        <View key={index} style={{ paddingHorizontal: 30, flexGrow: 1 }}>
          <View
            style={{
              display: 'flex',
              width: '100%',
              paddingHorizontal: 10,
              paddingVertical: products.length === 5 ? 10 : 20,
              position: 'relative',
              flexGrow: 1,
            }}
          >
            <View
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderWidth: 0.5,
                borderLeftWidth: 0.5,
                borderRightWidth: 0.5,
                borderTopWidth: index === 0 ? 0.5 : 0,
                borderStyle: 'solid',
                borderColor: '#000000',
                opacity: 0.1,
              }}
            />
            <View style={{ overflow: 'hidden', height: '100%' }}>
              {products.length < 4 && (
                <View style={{ flexShrink: 0 }}>
                  <Text
                    style={{
                      fontFamily: 'Gotham Rounded',
                      fontWeight: 600,
                      fontSize: 12,
                      color: '#000000',
                      opacity: 0.8,
                      flexShrink: 0,
                    }}
                  >
                    {product.title}
                  </Text>
                  <Text
                    style={{
                      fontFamily: 'Gotham Rounded',
                      fontWeight: 600,
                      fontSize: 6,
                      color: '#000000',
                      opacity: 0.8,
                      paddingTop: 3,
                      flexShrink: 0,
                    }}
                  >
                    {product.subtitle}
                  </Text>
                </View>
              )}
              {products.length === 1 && typeof product.image !== 'undefined' && (
                <View style={{ marginHorizontal: 'auto', flexGrow: 1, minHeight: '10%' }}>
                  <ImageBlockImage
                    image={{ content: product.image, settings: product.image_settings }}
                    style={{ width: '500', height: '600', objectFit: 'contain' }}
                  />
                </View>
              )}
              <View
                style={[
                  {
                    display: 'flex',
                    flexDirection: 'row',
                    flexGrow: 1,
                  },
                  products.length === 1 ? { flexShrink: 0 } : { flexShrink: 1 },
                ]}
              >
                {products.length === 1 ? (
                  <View style={{ paddingTop: 0, flexGrow: 1, flexShrink: 0 }}>
                    <ProductFeatures {...product} style={{ flexShrink: 0 }} />
                    <ProductNotes {...product} style={{ paddingTop: 10, flexShrink: 0 }} />
                  </View>
                ) : (
                  <View style={{ display: 'flex', flexDirection: 'row', flexGrow: 1 }}>
                    <View
                      style={[
                        products.length === 4
                          ? { width: 120 }
                          : products.length === 5
                          ? { width: 100 }
                          : { width: 150 },
                        { flexShrink: 0, justifyContent: 'space-between' },
                      ]}
                    >
                      <View
                        style={[
                          products.length === 4
                            ? { width: '100%', maxHeight: 120 }
                            : products.length === 5
                            ? { width: '100%', maxHeight: 100 }
                            : { width: '100%', maxHeight: 150 },
                          { flexGrow: 0, flexShrink: 1 },
                        ]}
                      >
                        {typeof product.image !== 'undefined' && (
                          <ImageBlockImage
                            image={{ content: product.image, settings: product.image_settings }}
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          />
                        )}
                      </View>

                      <ProductCerts {...product} />
                    </View>
                    <View style={[products.length < 4 ? { paddingTop: 10 } : {}, { paddingLeft: 10, flexGrow: 1 }]}>
                      {products.length >= 4 ? (
                        <>
                          <Text
                            style={{
                              fontFamily: 'Gotham Rounded',
                              fontWeight: 600,
                              fontSize: 12,
                              color: '#000000',
                              opacity: 0.8,
                              flexShrink: 0,
                            }}
                          >
                            {product.title}
                          </Text>
                          <Text
                            style={{
                              fontFamily: 'Gotham Rounded',
                              fontWeight: 600,
                              fontSize: 6,
                              color: '#000000',
                              opacity: 0.8,
                              paddingTop: 3,
                              flexShrink: 0,
                            }}
                          >
                            {product.subtitle}
                          </Text>
                          <View
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              paddingTop: 12,
                              flexShrink: 0,
                            }}
                          >
                            <View style={{ paddingRight: 12, flexShrink: 0 }}>
                              <ProductDocumentsAndDownloads {...product} condensed />
                            </View>
                            <View style={{ flexGrow: 1 }}>
                              <ProductFeatures {...product} />
                            </View>
                          </View>
                        </>
                      ) : (
                        <ProductFeatures {...product} style={{ flexShrink: 0 }} />
                      )}
                      <View
                        style={{
                          paddingTop: products.length === 5 ? 5 : 20,
                          flexShrink: 0,
                        }}
                      >
                        <ProductNotes {...product} />
                      </View>
                    </View>
                  </View>
                )}

                <View
                  style={[
                    products.length < 4
                      ? {
                          display: 'flex',
                          justifyContent: 'space-between',
                          height: '100%',
                        }
                      : {},
                    products.length === 1 ? { width: 163, paddingLeft: 25 } : { width: 148, paddingLeft: 10 },
                    { flexShrink: 0 },
                  ]}
                >
                  <View style={{ flexShrink: 0 }}>
                    <ProductPrices {...product} style={products.length === 1 ? { paddingBottom: 10 } : undefined} />

                    <ProductFinishes {...product} style={products.length === 1 ? { paddingBottom: 10 } : undefined} />
                  </View>

                  {products.length < 4 && (
                    <ProductDocumentsAndDownloads
                      {...product}
                      prominent={products.length === 1}
                      style={products.length === 1 ? { paddingBottom: 10 } : undefined}
                    />
                  )}
                  {products.length === 1 && <ProductCerts {...product} />}
                </View>
              </View>
            </View>
          </View>
        </View>
      ))}
    </View>
  );
};

export default Products;
